'use client';

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

import { IconButton } from '@flipster/react/design-system/icon-button';
import { StatusBadge } from '@flipster/react/design-system/status-badge';
import AlertIcon from '@flipster/react/icon/generated/AlertIcon';
import { useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { useSteps } from '@flipster/react/misc/step/use-steps';
import { vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { NotificationCenterEvent } from '@flipster/utils/segment/events/notification-center';

import { notificationCenterBunja } from './bunja';
import { NotificationList } from './notification-list';
import { NotificationSettings } from './notification-settings';

type NotificationCenterSteps = {
  list: undefined;
  settings: undefined;
};

const Steps = createStepComponent<NotificationCenterSteps>();

export const NotificationCenter = () => {
  const { notificationsAtom, isNotificationCenterOpenAtom, newFeatureCountAtom } = useBunja(notificationCenterBunja);
  const [isOpen, setIsOpen] = useAtom(isNotificationCenterOpenAtom);

  const notifications = useAtomValue(notificationsAtom);
  const newFeatureCount = useAtomValue(newFeatureCountAtom);
  const [lastViewedTime, setLastViewedTime] = useLocalStorage({
    key: 'flipster.configs.v1.lastViewedNotificationTime',
    defaultValue: null as string | null,
  });

  const hasUnreadNotifications = useMemo(() => {
    if (!notifications.length || !lastViewedTime) {
      return notifications.filter(({ isRead }) => !isRead).length > 0;
    }

    return notifications.some((notification) => notification.createdAt > lastViewedTime && !notification.isRead);
  }, [notifications, lastViewedTime]);

  useEffect(() => {
    if (isOpen && notifications.length > 0) {
      const latestNotificationTime = notifications[0].createdAt;
      setLastViewedTime(latestNotificationTime);
    }
  }, [isOpen, notifications, setLastViewedTime]);

  useEffect(() => {
    if (isOpen) {
      segment.page(NotificationCenterEvent.NotiCenterPageView, {
        has_new_feature_update: newFeatureCount > 0,
        new_feature_count: newFeatureCount,
      });
    }
  }, [isOpen, notifications, newFeatureCount]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [
      offset({
        mainAxis: 8,
        crossAxis: 170,
      }),
      flip({ padding: 16 }),
      shift({ padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const { current, push, back } = useSteps<NotificationCenterSteps>({
    initialValue: { step: 'list', context: undefined },
  });

  useEffect(() => {
    if (!isOpen) {
      push({ step: 'list', context: undefined });
    }
  }, [isOpen, push]);

  return (
    <>
      <IconButton
        ref={refs.setReference}
        {...getReferenceProps()}
        hierarchy='quaternary'
        icon={
          <StatusBadge
            visible={hasUnreadNotifications}
            positionType='icon'
            css={{
              _after: {
                right: -2,
                top: -1,
              },
            }}
          >
            <AlertIcon />
          </StatusBadge>
        }
      />
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={vstack({
              zIndex: 1000,
              w: 350,
              bgColor: 'background.default.level-1',
              px: 20,
              pt: 20,
              rounded: 8,
              boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.25)',
            })}
          >
            <Steps model={current}>
              <Steps.Step step='list'>
                {() => <NotificationList onSettingsClick={() => push({ step: 'settings', context: undefined })} />}
              </Steps.Step>
              <Steps.Step step='settings'>{() => <NotificationSettings onBackClick={back} />}</Steps.Step>
            </Steps>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
